import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useLocalSearchParams } from 'expo-router';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { Text as RNText } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import { QuestionGroupHoverCard } from '@/components/inbox/QuestionGroupHoverCard';
import { QuestionGroupTip } from '@/components/inbox/QuestionGroupTip';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { VStack } from '@/components/ui/vstack';
import { useOpenInquiryChat } from '@/hooks/inbox/useOpenInquiryChat';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { useInboxQuestionGroupCardsStore } from '@/services/stores/inboxQuestionGroupCardsStore';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import {
  buildQuestionGroupHeading,
  buildQuestionGroupTitle,
  getQuestionGroupCaseLine,
  formatLastNames,
  formatQuestionGroupNames,
  getBothSidesExperts,
  getQuestionGroupBadge,
  getQuestionGroupConversationIds,
  getQuestionGroupExpertEntries,
  getQuestionGroupLines,
  getQuestionGroupOpenTarget,
  getQuestionGroupRows,
  getUnreadSubmitterThreads,
  getRowPreview,
  isThreadWaiting,
  getThreadStatus,
  summarizeBothSidesQuestionGroup,
  summarizeQuestionGroup,
  type QuestionGroupBadge,
  type QuestionGroup,
  type QuestionGroupLine,
  type QuestionGroupRow,
} from '@/util/questionGroups';

/** The inquiry group card's avatar slot, sized to sit beside its two text rows. */
const AVATAR_SLOT = 40;

const SUBMITTER_AVATAR_SIZE = 36;

/** Line 2 of a sent thread the expert has not answered. */
const NO_REPLY_LABEL = 'Awaiting response';

/** Line 2 of a picked expert's row before staff send the question on to them. */
const NOT_RELAYED_LABEL = 'Not sent yet';

/** Line 2 for a group with no expert yet, so the header never shows a blank line. */
const NO_EXPERTS_LABEL = 'No experts yet';

/** The experts' faces tucked against the submitter's, small enough to read as "sent to". */
const EXPERT_FACE_SIZE = 20;

/** How far each further expert face sits left of the one before, so they overlap. */
const EXPERT_FACE_STEP = 11;

/** One expert face in the card header. */
type QuestionGroupFace = { key: string; name: string; avatar: QuestionGroupRow['avatar'] };

/**
 * The experts a card's header shows, each once by identity rather than by name, so two experts
 * who share a name are two faces. A `both` card reads its expert lines, the same list the open card
 * draws; any other card reads its sent rows, whose thread is addressed to the expert.
 */
function getQuestionGroupFaces(
  rows: QuestionGroupRow[],
  lines: QuestionGroupLine[],
  isBothSides: boolean
): QuestionGroupFace[] {
  if (isBothSides) {
    return lines.flatMap((line) =>
      line.kind === 'expert' && line.rows.length
        ? [{ key: line.key, name: line.name, avatar: line.avatar }]
        : []
    );
  }

  const seen = new Set<string>();

  return rows.flatMap((row) => {
    const key =
      row.thread.recipient?.id === undefined ? row.name : `expert-${row.thread.recipient.id}`;

    if (row.fromSubmitter || seen.has(key)) {
      return [];
    }

    seen.add(key);

    return [{ key, name: row.name, avatar: row.avatar }];
  });
}

/**
 * The card's face is the submitter's, since the title names them. Once staff send the question
 * on, up to three of the experts' faces sit small against it, so the card still shows who it went
 * to without putting an expert's face beside the submitter's name. Before that, a count says how
 * many threads the submitter opened. The count is drawn, not spoken: the card's accessible name
 * already says how many experts there are.
 */
function QuestionGroupAvatars({
  experts,
  submitterThreadCount,
  submitter,
}: {
  /** The experts the question was sent to, each once. */
  experts: QuestionGroupFace[];
  submitterThreadCount: number;
  submitter: InquiryV2['author'] | null | undefined;
}) {
  const expertRows = experts.slice(0, 3);

  const uri = getAvatarUrl(submitter?.avatar ?? undefined);

  return (
    <Box className="relative" style={{ width: AVATAR_SLOT, height: AVATAR_SLOT }}>
      <Avatar size="xs" style={{ width: SUBMITTER_AVATAR_SIZE, height: SUBMITTER_AVATAR_SIZE }}>
        <AvatarImage
          source={uri ? { uri } : primum_default_avatar}
          alt={[submitter?.firstName, submitter?.lastName].filter(Boolean).join(' ')}
        />
      </Avatar>
      {expertRows.map((row, index) => {
        const expertUri = getAvatarUrl(row.avatar ?? undefined);

        // Hovering a face on the web names the expert. Screen readers skip the face: the card's
        // own label and its rows already say who the experts are.
        return (
          <QuestionGroupHoverCard key={row.key} enabled text={row.name}>
            {(triggerProps) => (
              <Avatar
                {...triggerProps}
                size="xs"
                aria-hidden
                testID="question-group-expert-face"
                className="absolute border border-background-0"
                style={{
                  right: -4 + index * EXPERT_FACE_STEP,
                  bottom: -2,
                  width: EXPERT_FACE_SIZE,
                  height: EXPERT_FACE_SIZE,
                  zIndex: expertRows.length - index,
                }}>
                <AvatarImage
                  source={expertUri ? { uri: expertUri } : primum_default_avatar}
                  alt=""
                />
              </Avatar>
            )}
          </QuestionGroupHoverCard>
        );
      })}
      {!expertRows.length && submitterThreadCount > 1 ? (
        <Box
          aria-hidden
          className="absolute -bottom-0.5 right-0 h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-background-0 bg-background-100 px-1">
          <Text className="font-medium text-2xs leading-none text-typography-800">
            {submitterThreadCount}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Two tones only. Unread is rust and semibold, matching the unread dot and the bold name, because
 * a new reply is the one state that asks for attention. Every other status, close requested,
 * closed and not sent, is the same gray and says what it is in words: separate hues for each were
 * tried and read as noise, and blue or green collided with the flat inbox's own badges. Each text
 * token clears AA on its own fill in both themes.
 */
const STATUS_BADGE_TONE: Record<QuestionGroupBadge['tone'], { shell: string; text: string }> = {
  unread: { shell: 'bg-tertiary-50 border-tertiary-300', text: 'font-semibold text-tertiary-700' },
  muted: { shell: 'bg-background-50 border-outline-300', text: 'text-typography-700' },
};

function StatusBadge({ label, tone }: { label: string; tone: QuestionGroupBadge['tone'] }) {
  const { shell, text } = STATUS_BADGE_TONE[tone];

  return (
    <Badge size="md" variant="outline" action="muted" className={twJoin('shrink-0', shell)}>
      <BadgeText size="sm" className={twJoin('normal-case', text)}>
        {label}
      </BadgeText>
    </Badge>
  );
}

function UnreadDot({ visible }: { visible: boolean }) {
  return (
    <Box
      testID={visible ? 'unread-indicator' : undefined}
      className={twJoin('h-2.5 w-2.5', visible && 'rounded-full bg-tertiary-400')}
    />
  );
}

/**
 * A submitter's own thread to Ask Anonymously is marked handled by staff rather than asked to be
 * closed by an expert, so its close request reads Handled, as the chat says it.
 */
function getThreadRowBadge(
  fromSubmitter: boolean,
  status: ReturnType<typeof getThreadStatus>
): QuestionGroupBadge | null {
  if (status === 'closed') {
    return { tone: 'muted', label: 'Closed' };
  }

  if (status === 'close_requested') {
    return { tone: 'muted', label: fromSubmitter ? 'Handled' : 'Close requested' };
  }

  return null;
}

/**
 * Line 2 for a row with nothing new to preview, so it says where it stands rather than staying
 * empty: a pick nobody has relayed to, or a sent thread the expert has not answered.
 */
function getRowStatusLine(notSent: boolean, thread: QuestionGroupRow['thread']) {
  if (notSent) {
    return NOT_RELAYED_LABEL;
  }

  return isThreadWaiting(thread) ? NO_REPLY_LABEL : null;
}

/**
 * The card's tree guide beside one row: a line running the row's full height, or on the last row
 * the curved elbow that ends it. Every level of the tree draws it, the card's rows and an entry's
 * nested Expert lines, and only where it sits differs, so the color, the 1px weight and the corner
 * radius live here once. `lineClass` and `elbowClass` place it.
 */
function TreeGuide({
  isLast,
  lineClass,
  elbowClass,
  testID,
}: {
  isLast: boolean;
  lineClass: string;
  elbowClass: string;
  testID?: string;
}) {
  return isLast ? (
    <Box
      aria-hidden
      testID={testID}
      className={twJoin(
        'absolute top-0 rounded-bl-[14px] border-b border-l border-outline-300',
        elbowClass
      )}
    />
  ) : (
    <Box
      aria-hidden
      testID={testID}
      className={twJoin('absolute bottom-0 top-0 border-l border-outline-300', lineClass)}
    />
  );
}

/** Where the card's own tree runs: left of its rows, under the avatar column. */
const CARD_GUIDE_LINE_CLASS = 'left-[29.5px]';

const CARD_GUIDE_ELBOW_CLASS = 'left-[29.5px] h-[20px] w-[14.5px]';

/**
 * A `both` card's lines sit where any card's rows do, at every width: the guide drops from the
 * center of the submitter's avatar and curves 14px short of each line's avatar. Phones once moved
 * the guide to the avatar's left edge for a little more text width, and it read as misaligned.
 */
const BOTH_ENTRY_INDENT_CLASS = 'pl-[48px]';

const BOTH_ENTRY_GUIDE_LINE_CLASS = CARD_GUIDE_LINE_CLASS;

const BOTH_ENTRY_GUIDE_ELBOW_CLASS = CARD_GUIDE_ELBOW_CLASS;

/** A side row's preview wraps to two lines on phones and stays one line at md and up. */
const SIDE_PREVIEW_LINES = { default: 2, md: 1 };

/**
 * A row mirrors the card header: the name and unread dot on the first line, the preview and the
 * status badge on the second, so a row's badge lines up under the header's.
 */
type QuestionGroupThreadRowProps = {
  row: QuestionGroupRow;
  isSelected: boolean;
  onOpen: (row: QuestionGroupRow) => void;
};

function QuestionGroupThreadRow({
  row,
  isSelected,
  onOpen,
  isLast,
}: QuestionGroupThreadRowProps & { isLast: boolean }) {
  const { thread, notSent, name: expertName } = row;

  const status = notSent ? 'open' : getThreadStatus(thread);

  const preview = getRowPreview(row);

  const secondLine = preview ?? getRowStatusLine(notSent, thread);

  const unread = Boolean(thread.hasUnread);

  // An unread reply stands out a step below the name: medium and dark, not a second semibold line.
  const previewTone = unread ? 'font-medium text-typography-900' : 'text-typography-700';

  const rowBadge = getThreadRowBadge(row.fromSubmitter, status);

  return (
    <Box className="relative pl-[48px]" testID={`question-group-thread-${thread.conversationId}`}>
      <TreeGuide
        isLast={isLast}
        lineClass={CARD_GUIDE_LINE_CLASS}
        elbowClass={CARD_GUIDE_ELBOW_CLASS}
      />
      <Pressable
        onPress={() => onOpen(row)}
        accessibilityRole="button"
        accessibilityLabel={`${expertName}${notSent ? ', not sent' : ''}${secondLine ? `, ${secondLine}` : ''}${unread ? ', unread' : ''}`}
        accessibilityState={{ selected: isSelected }}
        className={twJoin(
          'my-0.5 mr-2 rounded-[10px] py-[7px] pl-2.5 pr-2 web:transition-colors web:duration-150',
          isSelected
            ? 'bg-background-100'
            : 'data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100'
        )}>
        <VStack className="gap-0.5">
          <HStack className="items-center justify-between gap-2">
            <Text
              numberOfLines={1}
              size="md"
              className={twJoin(
                'min-w-0 flex-1',
                unread && 'font-semibold text-typography-900',
                !unread && isSelected && 'text-typography-900',
                !unread && !isSelected && 'text-typography-800'
              )}>
              {expertName}
            </Text>
            <UnreadDot visible={unread} />
          </HStack>
          {secondLine || rowBadge ? (
            <HStack className="items-center gap-1.5">
              <Text numberOfLines={1} size="sm" className={twJoin('min-w-0 flex-1', previewTone)}>
                {secondLine}
              </Text>
              {rowBadge ? <StatusBadge label={rowBadge.label} tone={rowBadge.tone} /> : null}
            </HStack>
          ) : null}
        </VStack>
      </Pressable>
    </Box>
  );
}

/**
 * What a screen reader hears for each side of an entry. Nothing is drawn: an entry lists its HCP
 * lines first and its Expert lines after, and the order carries the side.
 */
const SIDE_LABELS: Record<'hcp' | 'expert', string> = {
  hcp: 'HCP thread',
  expert: 'Expert thread',
};

type QuestionGroupSideRowProps = {
  side: 'hcp' | 'expert';
  row: QuestionGroupRow;
  isSelected: boolean;
  onOpen: (row: QuestionGroupRow) => void;
  /** What an HCP line says when its thread has nothing of its own: the question the card is about. */
  questionText?: string;
  /** The person whose name heads this line, drawn inside the press target so the name opens it too. */
  heading?: LineHeading;
  /** Whether any line of the heading's entry is unread, which sets the name's weight. */
  headingUnread?: boolean;
  /** How many HCP inquiries the heading's line stands for, said beside the name when more than one. */
  inquiryCount?: number;
};

/**
 * One thread of an expert's entry: the preview or where the thread stands, the
 * status badge and the unread dot. It is its own press target, at least 44px tall, so the HCP's
 * thread and the relay to the expert open separately.
 *
 * The first line under an entry's name carries that name inside its own press target, so a tap on
 * the name opens the thread the name stands for rather than landing on dead text above it. Further
 * lines under the same name, such as a repeat relay, sit below it on their own.
 */
function QuestionGroupSideRow({
  side,
  row,
  isSelected,
  onOpen,
  questionText = '',
  heading,
  headingUnread = false,
  inquiryCount = 1,
}: QuestionGroupSideRowProps) {
  const { thread, notSent, name } = row;

  const previewLines = useBreakpointValue(SIDE_PREVIEW_LINES);

  const status = notSent ? 'open' : getThreadStatus(thread);

  // An HCP line is never blank: where the thread has no state to word, it shows its own latest
  // message even when closed, and then the question itself, so the badge always has text to follow.
  const secondLine =
    getRowPreview(row) ??
    getRowStatusLine(notSent, thread) ??
    (side === 'hcp' ? thread.lastMessagePreview?.trim() || questionText || null : null);

  const unread = Boolean(thread.hasUnread);

  const rowBadge = getThreadRowBadge(row.fromSubmitter, status);

  const previewTone = unread ? 'font-medium text-typography-900' : 'text-typography-700';

  const accessibilityLabel = [
    heading ? getHeadingLabel(heading) : name,
    SIDE_LABELS[side],
    inquiryCount > 1 ? `${inquiryCount} inquiries` : null,
    notSent ? 'not sent' : null,
    secondLine,
    rowBadge?.label.toLowerCase(),
    unread ? 'unread' : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Pressable
      onPress={() => onOpen(row)}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected: isSelected }}
      testID={`question-group-thread-${thread.conversationId}`}
      className={twJoin(
        'mr-2 min-h-[44px] justify-center rounded-[10px] py-1.5 pl-2.5 pr-2 web:transition-colors web:duration-150',
        isSelected
          ? 'bg-background-100'
          : 'data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100'
      )}>
      <HStack className="gap-2">
        {/* A further line under the same name keeps the avatar's width, so its text lines up. */}
        {heading ? <RowAvatar avatar={heading.avatar} /> : <Box className="w-5" />}
        <VStack className="min-w-0 flex-1">
          {heading ? (
            <HeadingNameLine heading={heading} unread={headingUnread} inquiryCount={inquiryCount} />
          ) : null}
          <HStack className="items-center gap-2">
            {secondLine ? (
              <Text
                numberOfLines={previewLines}
                size="sm"
                className={twJoin('min-w-0 flex-1', previewTone)}>
                {secondLine}
              </Text>
            ) : null}
            {/* Only when there is nothing at all to say: the spacer keeps the badge at the right edge. */}
            {secondLine ? null : <Box className="min-w-0 flex-1" />}
            {rowBadge ? <StatusBadge label={rowBadge.label} tone={rowBadge.tone} /> : null}
            <UnreadDot visible={unread} />
          </HStack>
        </VStack>
      </HStack>
    </Pressable>
  );
}

/** Beside a line's name, so each face in the card's header has a name. */
const ROW_AVATAR_SIZE = 20;

/** Who heads a line of the card: their name, their face, and why they are there when it needs saying. */
type LineHeading = {
  name: string;
  avatar: QuestionGroupRow['avatar'];
  aside: string | null;
};

/** A heading's name and aside, as a screen reader hears them. */
function getHeadingLabel(heading: LineHeading) {
  return heading.aside ? `${heading.name} ${heading.aside}` : heading.name;
}

/**
 * The face of the person a line's name stands for. It is decoration, since the name beside it
 * says who it is, so a screen reader skips it.
 */
function RowAvatar({ avatar }: { avatar: QuestionGroupRow['avatar'] }) {
  const uri = getAvatarUrl(avatar ?? undefined);

  return (
    <Avatar
      size="xs"
      aria-hidden
      className="mt-0.5"
      style={{ width: ROW_AVATAR_SIZE, height: ROW_AVATAR_SIZE }}>
      <AvatarImage source={uri ? { uri } : primum_default_avatar} alt="" />
    </Avatar>
  );
}

/** A line's name, then its aside and, on an HCP line folding several inquiries, how many. */
function HeadingNameLine({
  heading,
  unread,
  inquiryCount = 1,
}: {
  heading: LineHeading;
  unread: boolean;
  inquiryCount?: number;
}) {
  const aside = [heading.aside, inquiryCount > 1 ? `${inquiryCount} inquiries` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <HStack className="items-center gap-2">
      <Text
        numberOfLines={1}
        size="md"
        className={twJoin(
          'shrink',
          unread ? 'font-semibold text-typography-900' : 'text-typography-800'
        )}>
        {heading.name}
      </Text>
      {aside ? (
        <Text numberOfLines={1} size="sm" className="shrink-0 text-typography-700">
          {aside}
        </Text>
      ) : null}
    </HStack>
  );
}

/**
 * The line of an expert the HCP asked for whom nobody has relayed to yet. There is no thread to
 * open, so it is plain text at the same height as its neighbors rather than a button that goes
 * nowhere.
 */
function NotSentExpertLine({ heading }: { heading: LineHeading }) {
  const previewLines = useBreakpointValue(SIDE_PREVIEW_LINES);

  return (
    <Box
      accessible
      accessibilityLabel={`${getHeadingLabel(heading)}, ${SIDE_LABELS.expert}, not sent`}
      testID="question-group-expert-not-sent"
      className="mr-2 min-h-[44px] justify-center py-1.5 pl-2.5 pr-2">
      <HStack className="gap-2">
        <RowAvatar avatar={heading.avatar} />
        <VStack className="min-w-0 flex-1">
          <HeadingNameLine heading={heading} unread={false} />
          <Text numberOfLines={previewLines} size="sm" className="text-typography-700">
            {NOT_RELAYED_LABEL}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}

type QuestionGroupLineViewProps = {
  line: QuestionGroupLine;
  isLast: boolean;
  selectedChatId?: string;
  onOpen: (row: QuestionGroupRow) => void;
  /** The question's own text, the last thing an HCP line falls back to. */
  questionText: string;
};

/**
 * One line of an expanded `both` card, every one at the same level: the HCP's line, or an
 * expert's. An expert relayed to more than once keeps every relay, the newest under the name and
 * the older ones below it.
 */
function QuestionGroupLineView({
  line,
  isLast,
  selectedChatId,
  onOpen,
  questionText,
}: QuestionGroupLineViewProps) {
  const heading: LineHeading =
    line.kind === 'hcp'
      ? { name: line.name, avatar: line.avatar, aside: 'to Ask Anonymously' }
      : { name: line.name, avatar: line.avatar, aside: line.note };

  return (
    <Box
      className={twJoin('relative', BOTH_ENTRY_INDENT_CLASS)}
      testID={`question-group-line-${line.key}`}>
      <TreeGuide
        isLast={isLast}
        lineClass={BOTH_ENTRY_GUIDE_LINE_CLASS}
        elbowClass={BOTH_ENTRY_GUIDE_ELBOW_CLASS}
      />
      <VStack className="py-0.5">
        {line.kind === 'hcp' ? (
          <QuestionGroupSideRow
            side="hcp"
            questionText={questionText}
            row={line.row}
            isSelected={selectedChatId === line.row.thread.conversationId}
            onOpen={onOpen}
            heading={heading}
            headingUnread={Boolean(line.row.thread.hasUnread)}
            inquiryCount={line.inquiryCount}
          />
        ) : null}
        {line.kind === 'expert' && !line.rows.length ? (
          <NotSentExpertLine heading={heading} />
        ) : null}
        {line.kind === 'expert'
          ? line.rows.map((row, index) => (
              <QuestionGroupSideRow
                key={row.thread.conversationId}
                side="expert"
                row={row}
                isSelected={selectedChatId === row.thread.conversationId}
                onOpen={onOpen}
                heading={index === 0 ? heading : undefined}
                headingUnread={line.rows.some((expertRow) => expertRow.thread.hasUnread)}
              />
            ))
          : null}
      </VStack>
    </Box>
  );
}

/**
 * Whether a one-line text is cut off with an ellipsis, so a hover card repeats the full text only
 * when there is more to see. Web only: it reads the rendered element's widths after layout, and
 * phones have no hover to show it on.
 */
function useIsTextTruncated() {
  const [element, setElement] = useState<HTMLElement | null>(null);

  const [isTruncated, setIsTruncated] = useState(false);

  // The app's Text renders a bare span on web, which never fires onLayout, so watch its size.
  const textRef = useCallback((node: RNText | null) => {
    setElement(isWeb ? (node as unknown as HTMLElement | null) : null);
  }, []);

  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const measure = () => setIsTruncated(element.scrollWidth > element.clientWidth);

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(element);

    return () => observer.disconnect();
  }, [element]);

  return { textRef, isTruncated };
}

/**
 * The slot after the caret that holds the unread dot. It has a floor of the dot plus a gap, so a
 * long title gives up its own width first and the dot never lands on top of the caret.
 */
export const UNREAD_DOT_SLOT_CLASS = 'min-w-[18px] flex-1 items-end pl-2';

/** The caret draws 20px, so its tap area is widened without widening its hover tint over the title. */
export const QUESTION_GROUP_CARET_HIT_SLOP = getAccessibleHitSlop(20);

/**
 * Whether the inquiry group header carries the selected tint. An open card tints the selected row inside
 * it, a thread or the submitter's question, so one selection never shows two highlights; a
 * collapsed card has no row to carry it, so its header does.
 */
export function isHeaderTinted(
  isQuestionGroupSelected: boolean,
  expanded: boolean,
  isRowSelected: boolean
) {
  return isQuestionGroupSelected && !(expanded && isRowSelected);
}

/**
 * The header's state fill. The selected tint when it carries the selection, otherwise a hover tint
 * only while the card is collapsed: an open card's header is not the control, so a hover tint on
 * it would read as a second selection beside the selected row.
 */
export function getHeaderStateClass(tinted: boolean, expanded: boolean) {
  if (tinted) {
    return 'bg-background-100';
  }

  return expanded ? '' : 'web:hover:bg-background-50';
}

type QuestionGroupCardProps = {
  questionGroup: QuestionGroup;
  currentTab: string;
  /** True for the one card that carries the one-time tip. */
  showTip?: boolean;
};

/**
 * A staff group: the original inquiry's cancer type, submitter and case line over the expert relay
 * threads sent out from it. Tapping the row opens a conversation exactly as a flat row does, and
 * a separate caret after the title shows or hides the threads. The header's own press area is not
 * a button to assistive technology, so the caret and the title button never sit inside another
 * button there; a press lands on the innermost target only, so nothing toggles twice.
 *
 * Staff only. The title names the submitter, so callers render this only for the Primum group.
 */
export const QuestionGroupCard = memo(function QuestionGroupCard({
  questionGroup,
  currentTab,
  showTip = false,
}: QuestionGroupCardProps) {
  const { threads } = questionGroup;

  const params = useLocalSearchParams<{ id?: string }>();

  const selectedChatId = params.id === 'undefined' ? undefined : params.id;

  const openChat = useOpenInquiryChat(currentTab);

  const isExpandedInStore = useInboxQuestionGroupCardsStore(
    (state) => state.expandedByKey[questionGroup.key] ?? false
  );

  const setExpanded = useInboxQuestionGroupCardsStore((state) => state.setExpanded);

  const isBothSides = questionGroup.side === 'both';

  const rows = useMemo(() => getQuestionGroupRows(questionGroup), [questionGroup]);

  const rowThreads = useMemo(() => rows.map((row) => row.thread), [rows]);

  const unreadSubmitterThreads = useMemo(
    () => getUnreadSubmitterThreads(questionGroup),
    [questionGroup]
  );

  /**
   * The submitter's unread follow-up has no row, so the header keeps its dot while open. A `both`
   * card's entries carry the HCP's threads, so its header has nothing left to carry.
   */
  const hasSubmitterUnread = !isBothSides && unreadSubmitterThreads.length > 0;

  /** A `both` card draws one entry per expert, each with an HCP and an Expert line. */
  const expertEntries = useMemo(
    () => (isBothSides ? getQuestionGroupExpertEntries(questionGroup) : []),
    [isBothSides, questionGroup]
  );

  const submitter = questionGroup.original.attributedSubmitter ?? questionGroup.original.author;

  const lines = useMemo(
    () => getQuestionGroupLines(expertEntries, submitter),
    [expertEntries, submitter]
  );

  const faces = useMemo(
    () => getQuestionGroupFaces(rows, lines, isBothSides),
    [rows, lines, isBothSides]
  );

  const [tipStep, setTipStep] = useState<1 | 2>(1);

  const title = useMemo(
    () => buildQuestionGroupTitle(questionGroup.original, questionGroup.originalGroup),
    [questionGroup.original, questionGroup.originalGroup]
  );

  const heading = useMemo(
    () => buildQuestionGroupHeading(questionGroup.original),
    [questionGroup.original]
  );

  const caseLine = getQuestionGroupCaseLine(questionGroup.original, questionGroup.originalGroup);

  const { textRef: caseLineRef, isTruncated: isCaseLineTruncated } = useIsTextTruncated();

  const summary = isBothSides
    ? summarizeBothSidesQuestionGroup(questionGroup)
    : summarizeQuestionGroup(rowThreads, {
        submitterSide: questionGroup.side === 'submitter',
      });

  const conversationIds = useMemo(
    () => getQuestionGroupConversationIds(questionGroup),
    [questionGroup]
  );

  const hasCaret = isBothSides ? conversationIds.length > 1 : rows.length > 1;

  const expanded = hasCaret && isExpandedInStore;

  const threadConversationIds = useMemo(
    () => threads.map((thread) => thread.conversationId),
    [threads]
  );

  const isQuestionGroupSelected =
    selectedChatId !== undefined && conversationIds.includes(selectedChatId);

  const isThreadSelected =
    selectedChatId !== undefined &&
    (isBothSides
      ? conversationIds.includes(selectedChatId)
      : rowThreads.some((thread) => thread.conversationId === selectedChatId));

  // A `both` summary already reads every thread of the card, so adding the header's own unread
  // submitter threads would count the same thread twice.
  const unreadCount = isBothSides
    ? summary.unreadCount
    : summary.unreadCount + unreadSubmitterThreads.length;

  const hasUnread = unreadCount > 0;

  const fanOutConversationIds = useMemo(
    () => questionGroup.originalGroup.map((inquiry) => inquiry.conversationId),
    [questionGroup.originalGroup]
  );

  const openRow = (row: QuestionGroupRow) =>
    openChat(row.thread, row.fromSubmitter ? fanOutConversationIds : threadConversationIds);

  const openQuestionGroup = () => {
    const target = getQuestionGroupOpenTarget(questionGroup);

    openChat(target.inquiry, target.siblingConversationIds);
  };

  const toggle = () => {
    setExpanded(questionGroup.key, !expanded);

    if (!expanded && tipStep === 1) {
      setTipStep(2);
    }
  };

  /**
   * An inquiry group with several threads expands on a tap and opens a conversation only from a thread row.
   * An inquiry group with one thread has nothing to expand, so a tap opens it, as a flat row does.
   * An open card whose submitter wrote an unread follow-up opens it from the header, since the
   * follow-up has no row of its own; the caret still closes the card.
   */
  const opensSubmitterThread = expanded && hasSubmitterUnread;

  const onHeaderPress = hasCaret && !opensSubmitterThread ? toggle : openQuestionGroup;

  const bothSidesExperts = useMemo(() => getBothSidesExperts(expertEntries), [expertEntries]);

  const expertCount = isBothSides ? bothSidesExperts.length : rows.length;

  const accessibleName = [
    title,
    `${expertCount} ${expertCount === 1 ? 'expert' : 'experts'}`,
    hasUnread ? `${unreadCount} unread` : null,
  ]
    .filter(Boolean)
    .join(', ');

  const questionGroupNames = useMemo(
    () =>
      isBothSides
        ? formatLastNames(bothSidesExperts.map((expert) => expert.lastName))
        : formatQuestionGroupNames(threads, questionGroup.notSentExperts),
    [isBothSides, bothSidesExperts, threads, questionGroup.notSentExperts]
  );

  const badge = getQuestionGroupBadge({ ...summary, unreadCount });

  return (
    <VStack className="px-3 py-1.5">
      <VStack
        testID={`question-group-card-${questionGroup.key}`}
        className="overflow-hidden rounded-[14px] border border-outline-100 bg-background-0">
        {/*
         * The whole header is one press target for the mouse and touch, so the padding and the
         * gaps between lines toggle the card too. It stays out of the accessibility tree: the
         * title button and the caret are what a keyboard or screen reader reaches.
         */}
        <Pressable
          accessible={false}
          tabIndex={-1}
          onPress={onHeaderPress}
          className={twJoin(
            'relative flex-row gap-2 pl-2.5 pr-4 pt-3.5 web:transition-colors web:duration-150',
            // Open with no case summary, the title is the header's only line, so it centers on the
            // avatar and the rows start sooner rather than leaving an empty strip under the title.
            expanded && !caseLine ? 'items-center pb-2' : 'items-start pb-3.5',
            getHeaderStateClass(
              isHeaderTinted(isQuestionGroupSelected, expanded, isThreadSelected),
              expanded
            )
          )}>
          {expanded ? (
            <Box
              aria-hidden
              className={twJoin(
                'absolute bottom-0 top-[58px] border-l border-outline-300',
                isBothSides ? BOTH_ENTRY_GUIDE_LINE_CLASS : CARD_GUIDE_LINE_CLASS
              )}
            />
          ) : null}
          <Box style={{ width: AVATAR_SLOT, height: AVATAR_SLOT }}>
            <QuestionGroupAvatars
              experts={faces}
              submitterThreadCount={
                // A `both` card's rows hold no HCP threads, so count the submitter's own threads.
                isBothSides
                  ? questionGroup.originalGroup.length
                  : rows.filter((row) => row.fromSubmitter).length
              }
              submitter={submitter}
            />
          </Box>

          <VStack className="min-w-0 flex-1 gap-0.5">
            <HStack className="items-center">
              <Pressable
                onPress={onHeaderPress}
                accessibilityRole="button"
                accessibilityLabel={accessibleName}
                accessibilityHint={
                  hasCaret && !opensSubmitterThread ? 'Shows each expert' : 'Opens the conversation'
                }
                accessibilityState={{
                  selected: isQuestionGroupSelected,
                  expanded: hasCaret ? expanded : undefined,
                }}
                className="min-w-0 shrink">
                <Text
                  numberOfLines={1}
                  size="md"
                  className={
                    hasUnread && (!expanded || hasSubmitterUnread)
                      ? 'font-semibold text-typography-900'
                      : 'text-typography-800'
                  }>
                  {heading}
                </Text>
              </Pressable>
              {hasCaret ? (
                <Pressable
                  onPress={toggle}
                  accessibilityRole="button"
                  accessibilityLabel={expanded ? 'Hide experts' : 'Show experts'}
                  accessibilityState={{ expanded }}
                  testID={`question-group-caret-${questionGroup.key}`}
                  hitSlop={QUESTION_GROUP_CARET_HIT_SLOP}
                  className="h-5 w-5 shrink-0 items-center justify-center rounded-md data-[hover=true]:bg-background-100">
                  <Icon
                    as={expanded ? ChevronUp : ChevronDown}
                    size="sm"
                    className="text-typography-700"
                  />
                </Pressable>
              ) : null}
              <Box className={UNREAD_DOT_SLOT_CLASS}>
                <UnreadDot visible={hasUnread && (!expanded || hasSubmitterUnread)} />
              </Box>
            </HStack>

            {caseLine ? (
              <QuestionGroupHoverCard enabled={isCaseLineTruncated} text={caseLine}>
                {(triggerProps) => (
                  <Pressable
                    {...triggerProps}
                    // After the spread: the hover card's trigger brings its own press handler.
                    accessible={false}
                    tabIndex={-1}
                    onPress={onHeaderPress}
                    className="min-w-0 max-w-full self-start">
                    <Text
                      ref={caseLineRef}
                      numberOfLines={1}
                      size="sm"
                      className="text-typography-800">
                      {caseLine}
                    </Text>
                  </Pressable>
                )}
              </QuestionGroupHoverCard>
            ) : null}

            {/* Expanded, the rows name the experts and carry their own dots, so this line would
                hold only the badge and leave a gap under the header. */}
            {expanded ? null : (
              <HStack className="items-center gap-1.5">
                <Text
                  numberOfLines={1}
                  size="sm"
                  className={twJoin('min-w-0 flex-1', 'text-typography-700')}>
                  {questionGroupNames || NO_EXPERTS_LABEL}
                </Text>
                {badge ? <StatusBadge label={badge.label} tone={badge.tone} /> : null}
              </HStack>
            )}
          </VStack>
        </Pressable>

        {expanded && isBothSides ? (
          <VStack className="pb-2.5">
            {lines.map((line, index) => (
              <QuestionGroupLineView
                key={line.key}
                line={line}
                isLast={index === lines.length - 1}
                selectedChatId={selectedChatId}
                onOpen={openRow}
                questionText={caseLine}
              />
            ))}
          </VStack>
        ) : null}

        {expanded && !isBothSides ? (
          <VStack className="pb-2.5">
            {rows.map((row, index) => (
              <QuestionGroupThreadRow
                key={row.thread.conversationId}
                row={row}
                isLast={index === rows.length - 1}
                isSelected={selectedChatId === row.thread.conversationId}
                onOpen={openRow}
              />
            ))}
          </VStack>
        ) : null}
      </VStack>

      {showTip && hasCaret ? (
        <QuestionGroupTip
          step={tipStep}
          onNext={() => {
            setExpanded(questionGroup.key, true);

            setTipStep(2);
          }}
          onBack={() => {
            setExpanded(questionGroup.key, false);

            setTipStep(1);
          }}
        />
      ) : null}
    </VStack>
  );
});
